import * as React from "react"
import { Redirect, Route } from "react-router-dom"
import { LocationDescriptor } from "history"
import { LOGIN_LANDING } from "constants/paths"
import {
	authenticate,
	authenticateResponse,
	FeatureProperties,
	logoutWithoutLoginRedirection,
	SessionDataStore,
} from "helpers/authUtils"
import { useApplicationContext } from "context/applicationContext"
import { UNAUTHORIZED_MESSAGE_CODE } from "components/LoginLanding/LoginLanding"
import {
	authorization,
	authorizationResponse,
} from "helpers/authorizationUtils"

interface LoginValidationProps {
	location: { search: string; pathname: string }
}

const defaultRedirect: LocationDescriptor = {
	pathname: LOGIN_LANDING,
	search: `message=${UNAUTHORIZED_MESSAGE_CODE}`,
}

const ValidateLoginCredentialsOrLogout: React.FC<LoginValidationProps> = ({
	location,
	...rest
}): JSX.Element => {
	const [redirectDestination, setRedirectDestination] = React.useState<
		LocationDescriptor
	>(defaultRedirect)
	const [hasRequestCompleted, setHasRequestCompleted] = React.useState<boolean>(
		false
	)
	const {
		setIsAuthenticated,
		setUsername,
		setUserInitials,
		setIsSupplier,
		setUserRoles,
		setEmail,
		setLastAcceptedTerms,
		setMedalliaLastUpdate,
		setPlans,
	} = useApplicationContext()

	const useAuthContext = (features: FeatureProperties | undefined) => {
		const sessionDataInstance = SessionDataStore.getInstance()
		sessionDataInstance.setFeatures(features || {})

		setUsername(sessionDataInstance.getUsername())
		setUserInitials(sessionDataInstance.getUserInitials())
		setIsSupplier(sessionDataInstance.getIsSupplier())
		setIsAuthenticated(sessionDataInstance.isAuthenticated())
		setUserRoles(sessionDataInstance.getRoles())
		setEmail(sessionDataInstance.getEmail())
		setLastAcceptedTerms(sessionDataInstance.getLastAcceptedTerms())
		setMedalliaLastUpdate(sessionDataInstance.getMedalliaLastUpdate())
		setPlans(sessionDataInstance.getPlans())
	}

	React.useEffect(() => {
		const queryParams = new Map(new URLSearchParams(location.search))
		const currentPath = location.pathname

		authenticate(currentPath, queryParams)
			.then(validateAuthenticationResponse)
			.then((res) => {
				setRedirectDestination(res.originPath)
				return res.roles
			})
			.then(authorization)
			.then(validateOrForceLogout)
			.then((res) => useAuthContext(res.features))
			.catch(() => {})
			.finally(() => setHasRequestCompleted(true))
	}, [])

	if (!hasRequestCompleted) {
		return <React.Fragment />
	}

	return (
		<Route {...rest} render={() => <Redirect to={redirectDestination} />} />
	)
}

const validateAuthenticationResponse = (
	response: authenticateResponse
): Promise<authenticateResponse> => {
	const { success } = response

	if (!success) {
		return Promise.reject("authentication failed")
	}

	return Promise.resolve(response)
}

const validateOrForceLogout = (
	response: authorizationResponse
): Promise<authorizationResponse> => {
	const { success, features } = response
	if (!success || !features) {
		return logoutWithoutLoginRedirection(true).then(() =>
			Promise.reject("authorization failed")
		)
	}

	return Promise.resolve(response)
}

export default ValidateLoginCredentialsOrLogout
